package com.arena_web.ui.sections.header.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.HeroIntroTiming
import com.arena_web.ui.utils.s
import com.arena_web.ui.utils.sGap
import com.arena_web.ui.utils.sHeight
import com.arena_web.ui.utils.sWidth
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.window
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Img

/**
 * Pro Play / Arena header brand mark — Figma 11287:211176.
 *
 * Three pieces: a 46×31 gradient ring glyph on the left, the "Arena"
 * gradient wordmark below, and a small light-blue "Pro Play" label hugging
 * the upper-right of the wordmark (all three are SVG art from the Figma
 * lockup).
 *
 * The SVGs ship with `preserveAspectRatio="none"`, so their rendered sizes
 * have to match the natural viewBox aspects (≈ 4.18:1 wordmark, ≈ 4.91:1
 * label, ≈ 1.47:1 ring glyph) or the artwork stretches. The aspect-locked
 * dimensions below preserve the shapes.
 */
// The glyph snaps in the instant the home page's flying hero logo lands on it.
val BrandGlyphDockKeyframes = Keyframes {
    from { Modifier.opacity(0) }
    each(15.percent) { Modifier.opacity(1) }
    to { Modifier.opacity(1) }
}

// The "Arena" wordmark + "Pro Play" label slide out from behind the glyph after it docks.
val BrandWordmarkRevealKeyframes = Keyframes {
    from { Modifier.opacity(0).styleModifier { property("transform", "translateX(-14px)") } }
    to { Modifier.opacity(1).styleModifier { property("transform", "translateX(0px)") } }
}

// [static] renders the brand mark with no intro animation and without the
// data-brand-glyph marker — for non-header placements (e.g. the footer), so the
// home hero's FLIP flight never confuses it with the header slot.
@Composable
fun BrandLogo(static: Boolean = false) {
    // The cinematic hand-off only makes sense on the home page, where HeroIntroLogo flies
    // into this slot; everywhere else the brand shows up immediately.
    val playIntro = remember { !static && window.location.pathname.trimEnd('/').isEmpty() }
    Row(
        // Figma lockup gap 6.29 × the 0.658 header scale.
        modifier = Modifier.sGap(4),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // 46×31 ring glyph — natural aspect 69.14 : 46.93 ≈ 1.47.
        // data-brand-glyph marks the landing target for the home hero's FLIP flight
        // (an attribute, not an id, because BrandLogo renders in several headers).
        // While waiting to dock it sits at opacity 0 but keeps its layout, so the
        // hero can measure its rect mid-intro.
        Img(
            src = AppIcons.ProPlayLogo,
            alt = "Pro Play Arena",
            attrs = Modifier
                .sWidth(46)
                .sHeight(31)
                .thenIf(!static) { Modifier.attrsModifier { attr("data-brand-glyph", "") } }
                .thenIf(playIntro) {
                    Modifier.opacity(0).animation(
                        BrandGlyphDockKeyframes.toAnimation(
                            duration = 600.ms,
                            delay = HeroIntroTiming.TOTAL_MS.ms,
                            timingFunction = AnimationTimingFunction.EaseOut,
                            fillMode = AnimationFillMode.Both,
                        )
                    )
                }
                .toAttrs(),
        )

        // Wordmark + "Pro Play" label stacked inside a positioned Box so the
        // label can sit absolutely against the top-right of the wordmark.
        // `position: relative` on the parent is essential — the absolute
        // children would otherwise position against the nearest positioned
        // ancestor (often the viewport).
        //
        // The wordmark keeps its historical 100px header width; the label is
        // scaled from the Figma 11287:211176 lockup by the same 100/121.18
        // factor and tucked against the wordmark's top like the pre-lockup
        // header (the Figma lockup's own vertical gap reads too airy at
        // header size).
        Box(
            modifier = Modifier
                .sWidth(100)
                .sHeight(32)
                .styleModifier { property("position", "relative") }
                .thenIf(playIntro) {
                    Modifier.opacity(0).animation(
                        BrandWordmarkRevealKeyframes.toAnimation(
                            duration = 450.ms,
                            delay = (HeroIntroTiming.TOTAL_MS + 120).ms,
                            timingFunction = AnimationTimingFunction.EaseOut,
                            fillMode = AnimationFillMode.Both,
                        )
                    )
                },
        ) {
            Img(
                src = AppIcons.ProPlayArenaWordmark,
                alt = "Arena",
                attrs = Modifier
                    .sWidth(100)
                    .sHeight(23.9)
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "0")
                        property("bottom", "0")
                        // Light theme: darken the white-gradient art so the
                        // wordmark stays legible (dark resolves `none`).
                        property("filter", "var(--l-logo-filter, none)")
                    }
                    .toAttrs(),
            )
            Img(
                src = AppIcons.ProPlayLabel,
                alt = "Pro Play",
                attrs = Modifier
                    .sWidth(51.9)
                    .sHeight(10.6)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "0")
                        property("right", "0")
                        property("filter", "var(--l-logo-filter, none)")
                    }
                    .toAttrs(),
            )
        }
    }
}
